import { useRef, useCallback, KeyboardEvent } from 'react';
import { PresetterMode, PRESETTER_MODES } from '../utils/constants';
import { formatNumber } from '../utils/binaryConversion';

interface BinaryInputGridProps {
  mode: PresetterMode;
  bytes: [string, string, string, string];
  onBitChange: (byteIndex: number, bitIndex: number, value: '0' | '1') => void;
  onPrefill: () => void;
  originalOffset: number;
}

export function BinaryInputGrid({
  mode,
  bytes,
  onBitChange,
  onPrefill,
  originalOffset
}: BinaryInputGridProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[][]>(
    Array(4).fill(null).map(() => Array(8).fill(null))
  );

  const dParams = PRESETTER_MODES[mode].dParams;

  const focusInput = useCallback((byteIndex: number, bitIndex: number) => {
    const input = inputRefs.current[byteIndex]?.[bitIndex];
    if (input) {
      input.focus();
      input.select();
    }
  }, []);

  const handleKeyDown = useCallback((
    e: KeyboardEvent<HTMLInputElement>,
    byteIndex: number,
    bitIndex: number
  ) => {
    const totalBits = 32;
    const currentIndex = byteIndex * 8 + bitIndex;

    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        if (currentIndex < totalBits - 1) {
          focusInput(Math.floor((currentIndex + 1) / 8), (currentIndex + 1) % 8);
        }
        break;
      case 'ArrowLeft':
        e.preventDefault();
        if (currentIndex > 0) {
          focusInput(Math.floor((currentIndex - 1) / 8), (currentIndex - 1) % 8);
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (byteIndex < 3) {
          focusInput(byteIndex + 1, bitIndex);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (byteIndex > 0) {
          focusInput(byteIndex - 1, bitIndex);
        }
        break;
      case '0':
      case '1':
        e.preventDefault();
        onBitChange(byteIndex, bitIndex, e.key as '0' | '1');
        // Auto-advance to next cell
        if (currentIndex < totalBits - 1) {
          focusInput(Math.floor((currentIndex + 1) / 8), (currentIndex + 1) % 8);
        }
        break;
      case 'Backspace':
      case 'Delete':
        e.preventDefault();
        onBitChange(byteIndex, bitIndex, '0');
        break;
    }
  }, [focusInput, onBitChange]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
          Original Offset (Binary Input)
        </h2>
        <button
          onClick={onPrefill}
          className="px-3 py-1.5 text-sm bg-industrial-500 hover:bg-industrial-600 text-white rounded-md transition-colors"
        >
          Pre-fill Bytes 2-3
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              <th className="px-2 py-1 text-left text-sm font-medium text-gray-600 dark:text-gray-400 w-20">
                D-Param
              </th>
              {[7, 6, 5, 4, 3, 2, 1, 0].map(bit => (
                <th
                  key={bit}
                  className="px-1 py-1 text-center text-xs font-medium text-gray-500 dark:text-gray-400 w-10"
                >
                  {bit}
                </th>
              ))}
              <th className="px-2 py-1 text-center text-sm font-medium text-gray-600 dark:text-gray-400 w-16">
                Hex
              </th>
            </tr>
          </thead>
          <tbody>
            {bytes.map((byte, byteIndex) => (
              <tr key={byteIndex} className="border-t border-gray-200 dark:border-gray-700">
                <td className="px-2 py-2 font-mono text-sm font-medium text-industrial-600 dark:text-industrial-400">
                  {dParams[byteIndex]}
                </td>
                {byte.split('').map((bit, bitIndex) => (
                  <td key={bitIndex} className="px-1 py-2">
                    <input
                      ref={el => {
                        if (!inputRefs.current[byteIndex]) {
                          inputRefs.current[byteIndex] = [];
                        }
                        inputRefs.current[byteIndex][bitIndex] = el;
                      }}
                      type="text"
                      inputMode="numeric"
                      value={bit}
                      onChange={() => {}} // Controlled by onKeyDown
                      onKeyDown={(e) => handleKeyDown(e, byteIndex, bitIndex)}
                      className={`
                        w-8 h-8 text-center font-mono text-lg rounded border-2
                        focus:outline-none focus:ring-2 focus:ring-industrial-500
                        ${bit === '1'
                          ? 'bg-industrial-100 dark:bg-industrial-900 border-industrial-400 dark:border-industrial-600 text-industrial-800 dark:text-industrial-200'
                          : 'bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                        }
                      `}
                      maxLength={1}
                      aria-label={`Byte ${byteIndex}, Bit ${7 - bitIndex}`}
                    />
                  </td>
                ))}
                <td className="px-2 py-2 font-mono text-sm text-gray-600 dark:text-gray-400 text-center">
                  {parseInt(byte, 2).toString(16).toUpperCase().padStart(2, '0')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600 dark:text-gray-400">Original Offset (Decimal):</span>
          <span className="font-mono text-lg font-bold text-gray-800 dark:text-gray-200">
            {formatNumber(originalOffset)}
          </span>
        </div>
      </div>

      <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
        Use arrow keys to navigate. Press 0 or 1 to enter values (auto-advances).
      </p>
    </div>
  );
}
